import { PDFDocument } from "pdf-lib"
import fontkit from "@pdf-lib/fontkit"
import firebase from "firebase"
import { auth, db, storage } from "../../../../utils/firebase"

// Utility functions
import { writeAgentBio, writeFooters, writeHeaders, writeIntroduction, writeItinerary } from "./drawText"

// Standalone, reusable quote PDF builder.
//
// This is the PDF render that used to live inline in rows/confirmed-package.js.
// It has been extracted here so it can be called BOTH from the build wizard
// (via confirmed-package.js) and from outside it (e.g. the post-generation
// tee-time editor on the enquiry Quotes tab - slice 6), without mounting a
// component. Behaviour is identical to the previous inline implementation.
//
// Input is a completed package plus its options/settings:
//   {
//     quoteID,              // quotes/{id} the PDF belongs to
//     details,              // the completed package/quote details object
//     agent,                // optional pre-fetched agent doc; fetched if absent
//     clientName,           // "First Last" for the introduction greeting
//     customIntroduction,
//     replaceFields,
//     offsetYear,
//     customDeposit,        // ctxDepositValue
//     alternativePricing,   // enquiry date-flexibility comparison (or null)
//   }
//
// Output: { url, bytes } - the storage download URL and the raw PDF bytes.
export const buildQuotePDF = async ({
	quoteID,
	details,
	agent = null,
	clientName,
	customIntroduction,
	replaceFields,
	offsetYear,
	customDeposit,
	alternativePricing = null,
}) => {
	// Base URL for the original template PDF
	const baseTemplate =
		"https://firebasestorage.googleapis.com/v0/b/ghd-booking-system.appspot.com/o/_system%2Fdocuments%2Fquoting%2FProject%20Eagle%20-%2003-11-25.pdf?alt=media&token=28a25a5e-56d1-4e50-bca2-0a2ea2fb519a"

	// Fetch this template as an array buffer
	const templatePDFBytes = await fetch(baseTemplate).then((response) => response.arrayBuffer())

	// Load the document up within the pdf-ib library
	const quotePDFDocument = await PDFDocument.load(templatePDFBytes)

	// Register the font kit with the PDF library
	quotePDFDocument.registerFontkit(fontkit)

	// Download a buffer version of the poppins font
	const poppins300Bytes = await fetch(
		"https://firebasestorage.googleapis.com/v0/b/ghd-booking-system.appspot.com/o/_system%2Ffonts%2FPoppins-Light.otf?alt=media&token=d9afcf78-2487-4443-b2cf-92e03fdd39fd"
	).then((res) => res.arrayBuffer())
	const poppins400Bytes = await fetch(
		"https://firebasestorage.googleapis.com/v0/b/ghd-booking-system.appspot.com/o/_system%2Ffonts%2FPoppins-Regular.otf?alt=media&token=99ebe70d-f9f6-454c-86f3-c00b9ae60423"
	).then((res) => res.arrayBuffer())
	const poppins600Bytes = await fetch(
		"https://firebasestorage.googleapis.com/v0/b/ghd-booking-system.appspot.com/o/_system%2Ffonts%2FPoppins-SemiBold.otf?alt=media&token=71d97025-3d30-4a6c-b662-4e7fdf051350"
	).then((res) => res.arrayBuffer())
	const poppins800Bytes = await fetch(
		"https://firebasestorage.googleapis.com/v0/b/ghd-booking-system.appspot.com/o/_system%2Ffonts%2FPoppins-ExtraBold.otf?alt=media&token=7bc212c3-b343-4d2f-8685-1ab6e785833a"
	).then((res) => res.arrayBuffer())

	// Embed the different fonts into the PDF
	const poppins300 = await quotePDFDocument.embedFont(poppins300Bytes)
	const poppins400 = await quotePDFDocument.embedFont(poppins400Bytes)
	const poppins600 = await quotePDFDocument.embedFont(poppins600Bytes)
	const poppins800 = await quotePDFDocument.embedFont(poppins800Bytes)

	// Pull out the various pages on the PDF
	const pages = quotePDFDocument.getPages()
	const firstPage = pages[0]

	// Setup some height/width variables for the actual page size
	const { height, width } = firstPage.getSize()

	// Get the current agent details (unless they were passed in pre-fetched)
	const agentDetails =
		agent ||
		(await db
			.doc(`users/${auth.currentUser.uid}`)
			.get()
			.then((agentDoc) => {
				return {
					...agentDoc.data(),
					name: `${agentDoc.data().first_name} ${agentDoc.data().last_name}`,
					phone: agentDoc.data().phone,
					email: agentDoc.data().email,
				}
			}))

	// Write the introduction paragraph
	await writeIntroduction({
		quotePDFDocument,
		agent: agentDetails,
		clientName,
		customIntroduction,
		introductionPage: pages[0],
		quoteDetails: details,
		sizes: { height, width },
		fonts: { poppins400, poppins600, poppins800 },
	})

	// Write the itinerary details onto the page. alternativePricing is only
	// present for enquiry builds with date flexibility on + a cheaper date;
	// it renders a single indicative comparison line below the totals. Tee
	// times (display-only, per round) render as itemised bullets under each
	// round's line item.
	await writeItinerary({ itineraryPage: pages[1], quoteDetails: details, sizes: { height }, fonts: { poppins400, poppins600 }, replaceFields, offsetYear, customDeposit, alternativePricing })

	// Write the agent bio
	await writeAgentBio({ quotePDFDocument, agent: agentDetails, agentBioPage: pages[2], quoteDetails: details, sizes: { height, width }, fonts: { poppins400, poppins600, poppins800 } })

	// Write the headers and footers onto the document
	await writeHeaders({ details, quotePDFDocument, sizes: { height, width }, fonts: { poppins400, poppins600, poppins800 } })
	await writeFooters({ agent: agentDetails, quotePDFDocument, sizes: { width }, fonts: { poppins300, poppins600 } })

	// Save the new PDF document
	const bytes = await quotePDFDocument.save()

	// Then we want to upload this into storage and return the resulting URL
	const url = await uploadQuotePDF({ quoteID, details, bytes })

	// Return both the URL and the raw bytes for the caller (the bytes are
	// reused to embed the PDF onto the outbound email as a base64 string).
	return { url, bytes }
}

// Handle the actual upload process of the file into the storage bucket
const monitorUploadProgress = (documentUploadRef) => {
	return new Promise((resolve) => {
		// Setup the listener on the state_change variable for the upload
		documentUploadRef.on(
			"state_changed",
			(snapshot) => {
				// Calculate a percentage for the upload progress
				const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
				console.log(`Uploading quote document...${progress}%`)
			},
			(error) => console.log(error),
			() => {
				resolve()
			}
		)
	})
}

// Upload the PDF quote into storage, write the documents subcollection entry
// and resolve with the download URL.
const uploadQuotePDF = async ({ quoteID, details, bytes }) => {
	// Establish some metadata for the document
	const meta = {
		contentType: "application/pdf",
		customMetadata: {
			fileID: details.reference,
			uploadedBy: auth.currentUser.uid,
		},
	}

	// Upload the document into storage
	const upload = storage.ref(`quotes/${quoteID}/${details.reference}`).put(bytes, meta)

	// Return a promise to halt execution until the upload is complete
	return new Promise((resolve) => {
		// Attach the monitor to the uploaded file
		monitorUploadProgress(upload).then(() => {
			// When it's complete, get the downloadURL from the callback
			upload.snapshot.ref.getDownloadURL().then(async (url) => {
				// Write this document into the quotes 'documents' subcollection
				await db.doc(`quotes/${quoteID}/documents/${details.reference}`).set({
					pdf_url: url,
					created: firebase.firestore.FieldValue.serverTimestamp(),
				})

				// Resolve the promise with the download URL
				resolve(url)
			})
		})
	})
}

// Convert the unit array data into a base64 string (used to embed the PDF onto
// the outbound quote email). Kept here alongside the builder so callers that
// need the email-ready string share one implementation.
export const pdfBytesToBase64 = async (bytes) => {
	// Use a FileReader to generate a base64 data URI
	const base64url = await new Promise((r) => {
		const reader = new FileReader()
		reader.onload = () => r(reader.result)
		reader.readAsDataURL(new Blob([bytes]))
	})

	// Complete result would show: "data:application/octet-stream;base64,<the data>", so just
	// return the remainder of the string behind the meta at the start
	return base64url.split(",", 2)[1]
}
